/**
 * Clean-room mosaic layout inspired by Telegram-iOS MosaicLayout principles.
 * Not a GPL port — patterns for 2–4 + simple rows for ≥5.
 */

export type MosaicItemPosition = {
  top: boolean;
  bottom: boolean;
  left: boolean;
  right: boolean;
  inside: boolean;
};

export type MosaicItemLayout = {
  frame: { x: number; y: number; width: number; height: number };
  position: MosaicItemPosition;
};

export type MosaicLayoutResult = {
  size: { width: number; height: number };
  items: MosaicItemLayout[];
};

type Size = { width: number; height: number };

const DEFAULT_SPACING = 1;

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function pos(
  top: boolean,
  bottom: boolean,
  left: boolean,
  right: boolean,
  inside = false,
): MosaicItemPosition {
  return { top, bottom, left, right, inside };
}

/**
 * @param maxSize bubble max box (e.g. 300×300)
 * @param itemSizes natural aspect sizes (width/height > 0)
 * @param spacing gap between tiles (TG default 1)
 */
export function chatMessageBubbleMosaicLayout(
  maxSize: Size,
  itemSizes: Size[],
  spacing = DEFAULT_SPACING,
): MosaicLayoutResult {
  const n = itemSizes.length;
  const maxW = Math.max(120, maxSize.width);
  const maxH = Math.max(120, maxSize.height);

  if (n === 0) {
    return { size: { width: 0, height: 0 }, items: [] };
  }

  if (n === 1) {
    const s = itemSizes[0];
    const ar = s.width / Math.max(1, s.height);
    let w = maxW;
    let h = w / ar;
    if (h > maxH) {
      h = maxH;
      w = h * ar;
    }
    w = clamp(Math.round(w), 80, maxW);
    h = clamp(Math.round(h), 74, maxH);
    return {
      size: { width: w, height: h },
      items: [
        {
          frame: { x: 0, y: 0, width: w, height: h },
          position: pos(true, true, true, true),
        },
      ],
    };
  }

  if (n === 2) {
    const w = maxW;
    const colW = (w - spacing) / 2;
    const h = clamp(Math.round(colW * 1.15), 120, maxH);
    return {
      size: { width: w, height: h },
      items: [
        {
          frame: { x: 0, y: 0, width: colW, height: h },
          position: pos(true, true, true, false),
        },
        {
          frame: { x: colW + spacing, y: 0, width: colW, height: h },
          position: pos(true, true, false, true),
        },
      ],
    };
  }

  if (n === 3) {
    const w = maxW;
    const leftW = Math.round(w * 0.58);
    const rightW = w - leftW - spacing;
    const h = clamp(Math.round(leftW * 1.05), 160, maxH);
    const halfH = (h - spacing) / 2;
    return {
      size: { width: w, height: h },
      items: [
        {
          frame: { x: 0, y: 0, width: leftW, height: h },
          position: pos(true, true, true, false),
        },
        {
          frame: { x: leftW + spacing, y: 0, width: rightW, height: halfH },
          position: pos(true, false, false, true),
        },
        {
          frame: {
            x: leftW + spacing,
            y: halfH + spacing,
            width: rightW,
            height: halfH,
          },
          position: pos(false, true, false, true),
        },
      ],
    };
  }

  if (n === 4) {
    const w = maxW;
    const cell = (w - spacing) / 2;
    const h = cell * 2 + spacing;
    const hh = Math.min(h, maxH);
    const cellH = (hh - spacing) / 2;
    return {
      size: { width: w, height: hh },
      items: [
        {
          frame: { x: 0, y: 0, width: cell, height: cellH },
          position: pos(true, false, true, false),
        },
        {
          frame: { x: cell + spacing, y: 0, width: cell, height: cellH },
          position: pos(true, false, false, true),
        },
        {
          frame: { x: 0, y: cellH + spacing, width: cell, height: cellH },
          position: pos(false, true, true, false),
        },
        {
          frame: {
            x: cell + spacing,
            y: cellH + spacing,
            width: cell,
            height: cellH,
          },
          position: pos(false, true, false, true),
        },
      ],
    };
  }

  // ≥5: rows of 2–3, target height ~ width*4/3 area
  const w = maxW;
  const cols = n <= 6 ? 2 : 3;
  const rows = Math.ceil(n / cols);
  const cellW = (w - spacing * (cols - 1)) / cols;
  const cellH = clamp(Math.round(cellW), 80, Math.floor(maxH / 2));
  const totalH = rows * cellH + (rows - 1) * spacing;
  const items: MosaicItemLayout[] = [];
  for (let i = 0; i < n; i++) {
    const r = Math.floor(i / cols);
    const c = i % cols;
    const rowCount =
      r === rows - 1 ? n - r * cols : cols;
    const isLastRow = r === rows - 1;
    const rowCellW =
      rowCount === cols
        ? cellW
        : (w - spacing * (rowCount - 1)) / rowCount;
    items.push({
      frame: {
        x: c * (rowCellW + spacing),
        y: r * (cellH + spacing),
        width: rowCellW,
        height: cellH,
      },
      position: pos(
        r === 0,
        isLastRow,
        c === 0,
        c === rowCount - 1,
        !(r === 0 || isLastRow || c === 0 || c === rowCount - 1),
      ),
    });
  }
  return { size: { width: w, height: Math.min(totalH, maxH) }, items };
}

/** Corner radii from mosaic position flags (outer 16 / inner 0). */
export function mosaicCornerRadii(
  p: MosaicItemPosition,
  outer = 16,
): string {
  const tl = p.top && p.left ? outer : 0;
  const tr = p.top && p.right ? outer : 0;
  const br = p.bottom && p.right ? outer : 0;
  const bl = p.bottom && p.left ? outer : 0;
  return `${tl}px ${tr}px ${br}px ${bl}px`;
}
